import {SidebarAbout} from '@github-ui/code-view-sidebar/components/SidebarAbout'
import {SidebarConfigCallToActionSkeleton} from '@github-ui/code-view-sidebar/components/SidebarConfigCallToAction'
import {SidebarContributorsSkeleton} from '@github-ui/code-view-sidebar/components/SidebarContributors'
import {SidebarDeploymentsSkeleton} from '@github-ui/code-view-sidebar/components/SidebarDeployments'
import {SidebarLanguagesSkeleton} from '@github-ui/code-view-sidebar/components/SidebarLanguages'
import {SidebarPackagesSkeleton} from '@github-ui/code-view-sidebar/components/SidebarPackages'
import {SidebarReleasesSkeleton} from '@github-ui/code-view-sidebar/components/SidebarReleases'
import {SidebarSponsorsSkeleton} from '@github-ui/code-view-sidebar/components/SidebarSponsors'
import {SidebarTemplateRepository} from '@github-ui/code-view-sidebar/components/SidebarTemplateRepository'
import {SidebarUsedBySkeleton} from '@github-ui/code-view-sidebar/components/SidebarUsedBy'
import {isReactCodeViewSidebarEnabled} from '@github-ui/code-view-sidebar/is-enabled'
import {CodeViewSidebarLayout} from '@github-ui/code-view-sidebar/layout'
import type {LicenseInfo, SidebarAboutResponse} from '@github-ui/code-view-sidebar/types'
import {type DataRouterFileOverviewPayload, PreferredFileTypes} from '@github-ui/code-view-types'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useQueryClient} from '@github-ui/react-query'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {Suspense, useCallback, useMemo} from 'react'

import {SidebarContributors} from './sidebar/SidebarContributors'
import {SidebarCTA} from './sidebar/SidebarCTA'
import {SidebarDeployments} from './sidebar/SidebarDeployments'
import {SidebarLanguages} from './sidebar/SidebarLanguages'
import {SidebarPackages} from './sidebar/SidebarPackages'
import {SidebarReleases} from './sidebar/SidebarReleases'
import {SidebarSponsors} from './sidebar/SidebarSponsors'
import {SidebarSuggestedWorkflows} from './sidebar/SidebarSuggestedWorkflows'
import {SidebarUsedBy} from './sidebar/SidebarUsedBy'

/** Data extracted from overviewFiles in the main payload. */
export interface OverviewFilesData {
  licenses?: LicenseInfo[]
  hasReadme: boolean
  hasCodeOfConduct: boolean
  hasContributing: boolean
  hasSecurityPolicy: boolean
}

export interface SidebarPayload {
  aboutData: SidebarAboutResponse | undefined
  queryKey: readonly unknown[]
}

export type OverviewSidebarProps = {
  overview: Pick<DataRouterFileOverviewPayload['overview'], 'overviewFiles'>
  sidebarPayload: SidebarPayload
}

/**
 * Extract sidebar-relevant data from overviewFiles.
 *
 * Reuses tree_payload_helper's proven resolution so we don't need
 * duplicate fields in the sidebarAbout endpoint.
 */
export function extractOverviewFilesData({
  overviewFiles,
}: OverviewSidebarProps['overview']): OverviewFilesData | undefined {
  if (!overviewFiles) return undefined
  const result: OverviewFilesData = {
    hasReadme: false,
    hasCodeOfConduct: false,
    hasContributing: false,
    hasSecurityPolicy: false,
  }
  const licenses: LicenseInfo[] = []
  let licenseIndex = 0

  for (const file of overviewFiles) {
    if (file.preferredFileType === PreferredFileTypes.LICENSE) {
      licenseIndex++
      licenses.push({name: file.tabName ?? 'License', index: licenseIndex, path: file.path, refName: file.refName})
    } else if (file.preferredFileType === PreferredFileTypes.README) {
      result.hasReadme = true
    } else if (file.preferredFileType === PreferredFileTypes.CODE_OF_CONDUCT) {
      result.hasCodeOfConduct = true
    } else if (file.preferredFileType === PreferredFileTypes.CONTRIBUTING) {
      result.hasContributing = true
    } else if (file.preferredFileType === PreferredFileTypes.SECURITY) {
      result.hasSecurityPolicy = true
    }
  }

  if (licenses.length > 0) {
    result.licenses = licenses
  }

  return result
}

function OverviewSidebarSection({
  children,
  fallback,
  errorFallback = null,
  loading,
}: {
  children: React.ReactNode
  fallback: React.ReactNode
  errorFallback?: React.ReactNode | null
  loading: boolean
}) {
  if (loading) {
    return <>{fallback}</>
  }
  return (
    <ErrorBoundary fallback={errorFallback}>
      <Suspense fallback={fallback}>{children}</Suspense>
    </ErrorBoundary>
  )
}

const MAX_DEPLOYMENTS_TO_SHOW = 3
/**
 * Sidebar orchestrator for the repository overview page.
 *
 * Receives `sidebarPayload` (about data + queryKey) from the parent
 * entrypoint. Each additional section uses `useSuspenseQuery` inside a
 * `<Suspense>` boundary — sections only fetch when they render (gated by
 * the `sections` config from about), avoiding unnecessary requests.
 */
export function OverviewSidebar({overview, sidebarPayload}: OverviewSidebarProps) {
  const {isServer} = useRuntimeEnvironment()
  const reactSidebarEnabled = isReactCodeViewSidebarEnabled()
  const overviewFiles = useMemo(() => extractOverviewFilesData(overview), [overview])
  const queryClient = useQueryClient()
  const {aboutData, queryKey} = sidebarPayload

  const invalidateAboutQuery = useCallback(() => {
    return queryClient.invalidateQueries({queryKey})
  }, [queryKey, queryClient])

  if (!aboutData || !overviewFiles || !reactSidebarEnabled) {
    return null
  }

  const {sections, ownerLogin, repoName} = aboutData

  return (
    <CodeViewSidebarLayout>
      {sections.cta && (
        <OverviewSidebarSection loading={isServer} fallback={<SidebarConfigCallToActionSkeleton />}>
          <SidebarCTA ownerLogin={ownerLogin} repoName={repoName} />
        </OverviewSidebarSection>
      )}

      <SidebarAbout
        className="hide-sm hide-md"
        {...aboutData}
        {...overviewFiles}
        onEditDialogSaved={invalidateAboutQuery}
      />

      {sections.releases && (
        <OverviewSidebarSection loading={isServer} fallback={<SidebarReleasesSkeleton />}>
          <SidebarReleases
            ownerLogin={ownerLogin}
            repoName={repoName}
            releaseCount={sections.releases.releaseCount}
            tagCount={sections.releases.tagCount}
          />
        </OverviewSidebarSection>
      )}

      {sections.sponsors && (
        <OverviewSidebarSection loading={isServer} fallback={<SidebarSponsorsSkeleton />}>
          <SidebarSponsors ownerLogin={ownerLogin} repoName={repoName} />
        </OverviewSidebarSection>
      )}

      {sections.deployments && (
        <OverviewSidebarSection
          loading={isServer}
          fallback={
            <SidebarDeploymentsSkeleton environmentNames={sections.deployments} maxToShow={MAX_DEPLOYMENTS_TO_SHOW} />
          }
        >
          <SidebarDeployments
            ownerLogin={ownerLogin}
            repoName={repoName}
            environmentNames={sections.deployments}
            maxToShow={MAX_DEPLOYMENTS_TO_SHOW}
          />
        </OverviewSidebarSection>
      )}

      {sections.packages && (
        <OverviewSidebarSection loading={isServer} fallback={<SidebarPackagesSkeleton />}>
          <SidebarPackages ownerLogin={ownerLogin} repoName={repoName} />
        </OverviewSidebarSection>
      )}

      {sections.usedBy && (
        <OverviewSidebarSection loading={isServer} fallback={<SidebarUsedBySkeleton />}>
          <SidebarUsedBy ownerLogin={ownerLogin} repoName={repoName} />
        </OverviewSidebarSection>
      )}

      {sections.contributors && (
        <OverviewSidebarSection loading={isServer} fallback={<SidebarContributorsSkeleton />}>
          <SidebarContributors ownerLogin={ownerLogin} repoName={repoName} />
        </OverviewSidebarSection>
      )}

      {sections.languages && (
        <OverviewSidebarSection loading={isServer} fallback={<SidebarLanguagesSkeleton />}>
          <SidebarLanguages ownerLogin={ownerLogin} repoName={repoName} />
        </OverviewSidebarSection>
      )}

      {aboutData.templateRepository && <SidebarTemplateRepository {...aboutData.templateRepository} />}

      {sections.suggestedWorkflows && (
        <OverviewSidebarSection loading={isServer} fallback={null}>
          <SidebarSuggestedWorkflows ownerLogin={ownerLogin} repoName={repoName} />
        </OverviewSidebarSection>
      )}
    </CodeViewSidebarLayout>
  )
}
